<script setup lang="ts">
import {ref} from "vue";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import Translator from "@/utils/common/Translator.ts";
import NamaIcon from "@/components/icons/NamaIcon.vue";
import ExpertModeIcon from "@/assets/editViewIcons/ExpertModeIcon.vue";
import LoginUtils from "@/utils/login/LoginUtils.ts";
import DTOAIChatSession = namaTypes.DTOAIChatSession;

const props = defineProps<{
  isHistoryOpen: boolean;
  isExpertMode: boolean;
  isRtl: boolean;
  isMiniState: boolean;
  chatHistoryList: DTOAIChatSession[];
  selectedChat: DTOAIChatSession | null;
}>();

const emit = defineEmits(['startNewChat', 'toggleHistory', 'toggleDirection', 'toggleExpertMode', 'selectChatSession', 'toggleMiniState']);
const drawer = ref(false);

function startNewChat() {
  useNamaFlagsStore().clearChatHistory();
  emit('startNewChat');
}

function toggleHistory() {
  emit('toggleHistory');
}

function toggleDirection() {
  emit('toggleDirection');
}

function toggleExpertMode() {
  emit('toggleExpertMode');
}

function selectChatSession(session: DTOAIChatSession) {
  emit('selectChatSession', session);
}

function toggleMiniState() {
  emit('toggleMiniState');
}

function formatChatDate(dateStr: Date | string | undefined): string {
  if (!dateStr)
    return '';

  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const chatDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  const time = date.toLocaleTimeString('en-US', {hour: 'numeric', minute: '2-digit', hour12: true});
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const day = String(date.getDate()).padStart(2, '0');
  const month = monthNames[date.getMonth()].toLowerCase();
  const year = date.getFullYear();

  if (chatDate.getTime() === today.getTime()) {
    return `Today, ${time}`;
  } else if (chatDate.getTime() === yesterday.getTime()) {
    return `Yesterday, ${time}`;
  } else {
    return `${day} ${month} ${year}, ${time}`;
  }
}

</script>

<template>
  <div class="tw-w-fit tw-pt-3 tw-pl-3 tw-flex tw-flex-col tw-space-y-2">
    <q-drawer
      v-model="drawer"
      show-if-above
      :mini="!drawer || isMiniState"
      :width="200"
      :breakpoint="500"
      bordered
      class="tw-flex tw-flex-col tw-pb-3 dark:tw-bg-namaBackground-dim-dark tw-bg-namaBackground-dim-light tw-text-namaFontColor-input-darkGray dark:tw-text-namaFontColor-input-dark"
    >
      <div class="tw-flex-none tw-shrink-0 tw-pt-3 tw-pl-3">
          <q-btn unelevated no-caps dense
                 @click="toggleMiniState"
                 :title="Translator.translateId((isMiniState?'open':'close') + Translator.translateId('SideBar'))"
                 class="tw-p-2 tw-rounded-md tw-text-namaFontColor-input-gray dark:tw-text-namaFontColor-input-dark">
            <NamaIcon default-icon="expand" class="tw-size-4  tw-rotate-90"/>
          </q-btn>
        </div>
      <div class="tw-flex-none tw-shrink-0">
        <q-list padding>
          <q-item v-ripple clickable @click="startNewChat" :title="Translator.translateId('startNewChat')">
            <q-item-section avatar>
              <NamaIcon default-icon="nm-group-block" class="tw-size-5"/>
            </q-item-section>
            <q-item-section v-text="Translator.translateId('startNewChat')"/>
          </q-item>
          <q-item v-ripple clickable :active="isRtl" @click="toggleDirection" :title="Translator.translateId('switchChatDirection')">
            <q-item-section avatar>
              <NamaIcon default-icon="compare_arrows" class="tw-size-5"
                        :class="[isRtl?'active-primary':'tw-text-[#374151] dark:tw-text-[#ffffff]']"/>
            </q-item-section>
            <q-item-section v-text="Translator.translateId('switchChatDirection')"/>
          </q-item>
          <q-item v-ripple clickable v-if="LoginUtils.currentUserIsAdminOrTreatAsAdmin()" :active="isExpertMode" @click="toggleExpertMode"
                  :title="Translator.translateId(isExpertMode ? 'deactivateExpertMode' : 'activateExpertMode')">
            <q-item-section avatar>
              <ExpertModeIcon class="tw-size-6"
                              :class="[isExpertMode?'tw-fill-primary-light dark:tw-fill-primary-dark':'tw-fill-[#374151] dark:tw-fill-[#ffffff]']"/>
            </q-item-section>
            <q-item-section v-text="Translator.translateId(isExpertMode ? 'deactivateExpertMode' : 'activateExpertMode')  "/>
          </q-item>
        </q-list>
      </div>
      <div class="tw-grow tw-overflow-y-auto tw-min-h-0 tw-pb-3">
        <q-list separator>
          <q-item clickable @click="toggleHistory" :title="Translator.translateId('chatHistory')"
                  class="tw-sticky tw-top-0 dark:tw-bg-namaBackground-dim-dark tw-bg-namaBackground-dim-light tw-z-10">
            <q-item-section class="tw-opacity-50" v-text="Translator.translateId('chatHistory')"/>
            <q-item-section avatar>
              <q-icon v-if="isMiniState" name="history" class="tw-size-4"/>
              <q-icon v-else name="keyboard_arrow_left" class="tw-size-4" :class="[isHistoryOpen?'tw-rotate-[-90deg]':'ltr:tw-rotate-180']"/>
            </q-item-section>
          </q-item>
              <q-item
                v-if="isHistoryOpen"
                v-for="chat in chatHistoryList"
                :key="chat.id"
                clickable
                @click="selectChatSession(chat)"
                :class="{ 'dark:tw-bg-primary-dark tw-bg-primary-light tw-text-white': selectedChat?.id === chat.id }">
                <q-item-section>
                  <q-item-label class="dark:tw-text-white">{{ chat.title || 'Untitled chat' }}</q-item-label>
                  <q-item-label v-if="isExpertMode" class="dark:tw-text-white">{{ Translator.translateRef(chat.chatUser) }}</q-item-label>
                  <q-item-label caption dir="ltr" class="dark:tw-text-white/70 rtl:tw-self-start">{{
                      formatChatDate(chat.createdAt)
                    }}
                  </q-item-label>
                </q-item-section>
              </q-item>
        </q-list>
      </div>
    </q-drawer>
  </div>
</template>

<style scoped>

</style>
